<template>
    <div class="popover-content"
         :class="{'popover-content-secondary': isDefault}">
        <v-payment-instrument
            :instrument="instrument"
            :show-sticky="showSticky"
            @open-sticky="showStickyModal"
            is-address-editable>
            <p slot="noInstrument" class="text-muted">This customer does not have a default payment instrument.</p>
        </v-payment-instrument>
        <sticky-gateway-modal
            v-if="isStickyModalVisible"
            :value="instrument.stickyGatewayAccountId"
            @close="hideStickyModal"
            @input="updateSelectedGatewayAccount"
        />
        <template v-if="$auth.editPaymentInstruments.isAllowed">
            <v-payment-card-expiration-form
                class="margin-top-m"
                v-if="instrument"
                v-show="showEditExpiration"
                :instrument="instrument"
                @cancel="showEditExpiration = false"
                @update="data => update({instrument, data})"
            />
            <div class="grid-fitted padding-top-m" v-if="instrument && !showEditExpiration">
                <div class="grid-item grid-item-third padding-right-xxs">
                    <div class="grid-item grid-item-third padding-right-xxs">
                        <template v-if="isDefault">
                            <r-button
                                fluid
                                v-if="instrument.isPaymentCard"
                                @click="showEditExpiration = true"
                                size="small">Update Exp.
                            </r-button>
                        </template>
                        <template v-else>
                            <r-button
                                fluid
                                class="padding-left-xs padding-right-xs"
                                @click="setDefaultPaymentInstrument"
                                size="small">Set as Primary
                            </r-button>
                        </template>
                    </div>
                </div>
                <div class="grid-item grid-item-third padding-left-xxs padding-right-xxs">
                    <template v-if="!isDefault">
                        <r-button
                            fluid
                            v-if="instrument.isPaymentCard"
                            @click="showEditExpiration = true"
                            size="small">Update Exp.
                        </r-button>
                    </template>
                </div>
                <div class="grid-item grid-item-third padding-left-xxs">
                    <r-button @click="deactivate"
                              fluid
                              size="small"
                              type="danger"
                              class="inline-s">Deactivate
                    </r-button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import vPaymentInstrument from '@/components/payment-instrument/v-payment-instrument';
    import {mapActions, mapState} from 'vuex';
    import vPaymentCardExpirationForm from '@/components/payment-instrument/v-payment-card-expiration-form';
    import StickyGatewayModal from '@/modules/customers/components/payment-instrument-manager/sticky-gateway-modal';

    export default {
        components: {
            vPaymentInstrument,
            vPaymentCardExpirationForm,
            StickyGatewayModal,
        },
        props: {
            instrument: {
                type: Object,
                default: null,
            },
            isDefault: {
                type: Boolean,
                default: false,
            },
        },
        provide() {
            return {
                updatePaymentInstrument: this.update,
            };
        },
        data() {
            return {
                showEditExpiration: false,
                isStickyModalVisible: false,
            };
        },
        computed: {
            ...mapState('customers', ['customer']),
            showSticky() {
                return !!(this.instrument && this.instrument.stickyGatewayAccountId);
            },
        },
        inject: ['getInstrumentApiPromise'],
        methods: {
            ...mapActions({
                updateCustomer: 'customers/set',
                removePaymentInstrument: 'customers/removePaymentInstrument',
                updatePaymentInstrument: 'customers/updatePaymentInstrument',
            }),
            async deactivate() {
                this.$emit('loading', true);
                const apiPromise = this.getInstrumentApiPromise(this.instrument, 'deactivate');
                const param = {
                    id: this.instrument.id,
                };
                try {
                    await this.$api(apiPromise(param));
                    this.removePaymentInstrument(this.instrument);
                    this.$toast.positive('Payment instrument successfully deactivated');
                } catch (error) {
                    this.$toast.fromError(error, 'Payment instrument could not be deactivated');
                } finally {
                    this.$emit('loading', false);
                }
            },
            async setDefaultPaymentInstrument() {
                this.$emit('loading', true);
                const defaultPaymentInstrument = this.instrument.getPaymentInstrumentModelPayload();
                const params = {
                    id: this.customer.id,
                    data: {
                        defaultPaymentInstrument,
                    },
                    expand: 'leadSource,defaultPaymentInstrument',
                };
                try {
                    const {fields: customer} = await this.$api(({customers}) => customers.update(params));
                    this.updateCustomer(customer);
                    this.$toast.positive('Default Payment instrument updated');
                } catch (error) {
                    this.$toast.fromError(error, 'Default Payment instrument could not be updated');
                } finally {
                    this.$emit('loading', false);
                }
            },
            async update({instrument, data}) {
                this.$emit('loading', true);
                const instumentToUpdate = instrument || this.instrument;
                try {
                    const apiPromise = this.getInstrumentApiPromise(instumentToUpdate, 'patch');
                    const param = {
                        id: instumentToUpdate.id,
                        data: {
                            ...instumentToUpdate.getPayload(),
                            ...data,
                        },
                    };
                    if (data.$isContactObject) {
                        if (!instrument.isPaymentCard) {
                            param.data = {
                                address: data.getPayload(),
                            };
                        } else {
                            param.data = {
                                billingAddress: data.getPayload(),
                            };
                        }
                    }
                    const {fields} = await this.$api(apiPromise(param));
                    this.updatePaymentInstrument(fields);
                    this.$toast.positive('Payment instrument successfully updated');
                } catch (error) {
                    this.$toast.fromError(error, 'Payment instrument could not be updated');
                } finally {
                    this.$emit('loading', false);
                    this.showEditExpiration = false;
                }
            },
            async updateSelectedGatewayAccount(stickyGatewayAccountId) {
                this.$emit('loading', true);
                this.hideStickyModal();

                const params = {
                    id: this.instrument.id,
                    data: {
                        ...this.instrument.getPayload(),
                        stickyGatewayAccountId,
                    },
                };
                try {
                    const apiPromise = this.getInstrumentApiPromise(this.instrument, 'patch');
                    const {fields} = await this.$api(apiPromise(params));
                    this.updatePaymentInstrument(fields);
                    this.$toast.positive(`Sticky gateway successfully changed to ${stickyGatewayAccountId}`);
                } catch (error) {
                    this.$toast.fromError(error, 'Unable to update the sticky gateway account. Please try again later or contact support.');
                } finally {
                    this.$emit('loading', false);
                }
            },
            showStickyModal() {
                this.isStickyModalVisible = true;
            },
            hideStickyModal() {
                this.isStickyModalVisible = false;
            },
        },
    };
</script>
